import { z } from 'zod';
import { contrast, HEX_RE, mix, readableOn, roundRatio } from './color';

/**
 * Tema token'ları. Tema editörünün değiştirebildiği her şey buradadır; serbest CSS yoktur. Değerler sıkı
 * doğrulanır (renk `#rrggbb`, sayı aralıkları, sabit seçenekler) ve vitrinde CSS değişkenlerine çevrilir.
 */

/** Pakete gömülü yazı tipleri (Google Fonts'a istek gitmez: KVKK yurt dışı aktarım yok). */
export const FONT_KEYS = [
  'plex_sans',
  'plex_condensed',
  'inter',
  'source_sans',
  'nunito_sans',
  'plex_serif',
  'lora',
  'merriweather',
] as const;
export type FontKey = (typeof FONT_KEYS)[number];

const ARABIC = "'IBM Plex Sans Arabic'";
export const FONTS: Record<FontKey, { name: string; family: string; kind: 'sans' | 'serif' }> = {
  plex_sans: {
    name: 'IBM Plex Sans',
    family: `'IBM Plex Sans', ${ARABIC}, system-ui, sans-serif`,
    kind: 'sans',
  },
  plex_condensed: {
    name: 'IBM Plex Sans Condensed',
    family: `'IBM Plex Sans Condensed', 'IBM Plex Sans', ${ARABIC}, system-ui, sans-serif`,
    kind: 'sans',
  },
  inter: { name: 'Inter', family: `'Inter', ${ARABIC}, system-ui, sans-serif`, kind: 'sans' },
  source_sans: {
    name: 'Source Sans 3',
    family: `'Source Sans 3', ${ARABIC}, system-ui, sans-serif`,
    kind: 'sans',
  },
  nunito_sans: {
    name: 'Nunito Sans',
    family: `'Nunito Sans', ${ARABIC}, system-ui, sans-serif`,
    kind: 'sans',
  },
  plex_serif: {
    name: 'IBM Plex Serif',
    family: `'IBM Plex Serif', ${ARABIC}, Georgia, serif`,
    kind: 'serif',
  },
  lora: { name: 'Lora', family: `'Lora', ${ARABIC}, Georgia, serif`, kind: 'serif' },
  merriweather: { name: 'Merriweather', family: `'Merriweather', ${ARABIC}, Georgia, serif`, kind: 'serif' },
};

export const PALETTE_KEYS = [
  'background',
  'surface',
  'text',
  'mutedText',
  'border',
  'primary',
  'secondary',
  'accent',
  'button',
  'buttonText',
  'buttonHover',
] as const;
export type PaletteKey = (typeof PALETTE_KEYS)[number];

const hex = z
  .string()
  .trim()
  .regex(HEX_RE, 'color_format')
  .transform((s) => s.toLowerCase());

export const PaletteSchema = z.strictObject(
  Object.fromEntries(PALETTE_KEYS.map((k) => [k, hex])) as Record<PaletteKey, typeof hex>,
);
export type Palette = z.output<typeof PaletteSchema>;

const int = (min: number, max: number) => z.number().int().min(min).max(max);

export const ThemeTokensSchema = z
  .strictObject({
    schema: z.literal(1),
    palette: PaletteSchema,
    /** Koyu görünüm renkleri; yoksa vitrin her zaman açık görünür. */
    darkPalette: PaletteSchema.nullable(),
    /** `auto`: ziyaretçinin cihazı koyu moddaysa koyu renkler. */
    colorScheme: z.enum(['light', 'auto']),
    typography: z.strictObject({
      bodyFont: z.enum(FONT_KEYS),
      headingFont: z.enum(FONT_KEYS),
      /** Gövde yazı boyutu (px). */
      baseSize: int(15, 19),
      headingScale: z.enum(['compact', 'normal', 'large']),
      headingWeight: z.union([z.literal(500), z.literal(600), z.literal(700)]),
    }),
    shape: z.strictObject({
      /** Kart ve alan köşe yarıçapı (px). */
      radius: int(0, 24),
      buttonShape: z.enum(['square', 'rounded', 'pill']),
    }),
    shadow: z.enum(['none', 'soft', 'strong']),
    card: z.strictObject({
      style: z.enum(['flat', 'bordered', 'raised']),
      imageRatio: z.enum(['square', 'portrait', 'landscape']),
    }),
    header: z.strictObject({
      layout: z.enum(['start', 'center']),
      style: z.enum(['surface', 'brand']),
      sticky: z.boolean(),
    }),
    footer: z.strictObject({ style: z.enum(['surface', 'dark', 'brand']) }),
    productGrid: z.strictObject({ columnsDesktop: int(2, 5), columnsMobile: int(1, 2) }),
    productPage: z.strictObject({ galleryPosition: z.enum(['start', 'end']) }),
    blog: z.strictObject({ layout: z.enum(['grid', 'list']) }),
    form: z.strictObject({ inputStyle: z.enum(['outline', 'filled']) }),
  })
  .refine((t) => t.colorScheme === 'light' || t.darkPalette !== null, {
    message: 'theme_dark_palette_required',
    path: ['colorScheme'],
  });
export type ThemeTokens = z.output<typeof ThemeTokensSchema>;

// ---------------------------------------------------------------------------
// Okunabilirlik (WCAG 2.1 AA, 1.4.3: normal metin için 4,5:1)
// ---------------------------------------------------------------------------

export const CONTRAST_RULES: ReadonlyArray<{ code: string; fg: PaletteKey; bg: PaletteKey; min: number }> = [
  { code: 'text_on_background', fg: 'text', bg: 'background', min: 4.5 },
  { code: 'text_on_surface', fg: 'text', bg: 'surface', min: 4.5 },
  { code: 'muted_on_background', fg: 'mutedText', bg: 'background', min: 4.5 },
  { code: 'muted_on_surface', fg: 'mutedText', bg: 'surface', min: 4.5 },
  { code: 'link_on_background', fg: 'primary', bg: 'background', min: 4.5 },
  { code: 'link_on_surface', fg: 'primary', bg: 'surface', min: 4.5 },
  { code: 'button_text', fg: 'buttonText', bg: 'button', min: 4.5 },
  { code: 'button_text_hover', fg: 'buttonText', bg: 'buttonHover', min: 4.5 },
];

export interface ContrastIssue {
  palette: 'light' | 'dark';
  code: string;
  fg: PaletteKey;
  bg: PaletteKey;
  ratio: number;
  min: number;
}

export function contrastIssues(tokens: Pick<ThemeTokens, 'palette' | 'darkPalette'>): ContrastIssue[] {
  const out: ContrastIssue[] = [];
  const check = (palette: Palette, which: 'light' | 'dark') => {
    for (const r of CONTRAST_RULES) {
      const ratio = contrast(palette[r.fg], palette[r.bg]);
      if (ratio < r.min)
        out.push({ palette: which, code: r.code, fg: r.fg, bg: r.bg, ratio: roundRatio(ratio), min: r.min });
    }
  };
  check(tokens.palette, 'light');
  if (tokens.darkPalette) check(tokens.darkPalette, 'dark');
  return out;
}

// ---------------------------------------------------------------------------
// Vitrin CSS değişkenleri
// ---------------------------------------------------------------------------

const SCALE = { compact: 1.2, normal: 1.25, large: 1.333 } as const;
const SHADOW = {
  none: 'none',
  soft: '0 1px 2px rgb(0 0 0 / 0.06), 0 4px 14px -6px rgb(0 0 0 / 0.14)',
  strong: '0 2px 4px rgb(0 0 0 / 0.08), 0 12px 28px -10px rgb(0 0 0 / 0.28)',
} as const;
const RATIO = { square: '1 / 1', portrait: '4 / 5', landscape: '4 / 3' } as const;

function paletteVars(p: Palette, prefix: string): Record<string, string> {
  return {
    [`${prefix}bg`]: p.background,
    [`${prefix}surface`]: p.surface,
    [`${prefix}surface-2`]: mix(p.surface, p.background, 0.5),
    [`${prefix}text`]: p.text,
    [`${prefix}muted`]: p.mutedText,
    [`${prefix}border`]: p.border,
    [`${prefix}border-strong`]: mix(p.border, p.text, 0.25),
    [`${prefix}primary`]: p.primary,
    [`${prefix}primary-tint`]: mix(p.primary, p.background, 0.88),
    [`${prefix}on-primary`]: readableOn(p.primary),
    [`${prefix}secondary`]: p.secondary,
    [`${prefix}on-secondary`]: readableOn(p.secondary),
    [`${prefix}accent`]: p.accent,
    [`${prefix}on-accent`]: readableOn(p.accent),
    [`${prefix}button`]: p.button,
    [`${prefix}button-text`]: p.buttonText,
    [`${prefix}button-hover`]: p.buttonHover,
    [`${prefix}footer-dark`]: p.text,
    [`${prefix}on-footer-dark`]: p.background,
  };
}

export interface ThemeCss {
  /** `<html>` öğesine satır içi stil olarak verilen CSS değişkenleri (değerler doğrulanmış). */
  style: Record<string, string>;
  /** `<html>` öğesinin `data-*` öznitelikleri (düzen seçimleri). */
  attrs: Record<string, string>;
}

/**
 * Token'ları vitrinin CSS değişkenlerine çevirir. Açık renkler `--thl-*`, koyu renkler `--thd-*`; vitrin
 * stil dosyası bunları kendi değişkenlerine bağlar (koyu renkler yalnızca `data-scheme="auto"` iken ve
 * cihaz koyu moddayken). Tüm değerler şemadan geçtiği için dizeye kullanıcı girdisi karışmaz.
 */
export function themeCss(tokens: ThemeTokens): ThemeCss {
  const t = tokens.typography;
  const s = SCALE[t.headingScale];
  const rem = (px: number) => `${Math.round((px / 16) * 1000) / 1000}rem`;
  const style: Record<string, string> = {
    ...paletteVars(tokens.palette, '--thl-'),
    ...(tokens.darkPalette ? paletteVars(tokens.darkPalette, '--thd-') : {}),
    '--th-font-body': FONTS[t.bodyFont].family,
    '--th-font-heading': FONTS[t.headingFont].family,
    '--th-base': `${t.baseSize}px`,
    '--th-h1': rem(t.baseSize * s ** 4),
    '--th-h2': rem(t.baseSize * s ** 2),
    '--th-h3': rem(t.baseSize * s),
    '--th-heading-weight': String(t.headingWeight),
    '--th-radius-md': `${tokens.shape.radius}px`,
    '--th-radius-sm': `${Math.round(tokens.shape.radius * 0.6)}px`,
    '--th-btn-radius':
      tokens.shape.buttonShape === 'pill'
        ? '999px'
        : tokens.shape.buttonShape === 'rounded'
          ? `${Math.max(4, Math.round(tokens.shape.radius * 0.6))}px`
          : '0px',
    '--th-shadow': SHADOW[tokens.shadow],
    '--th-card-ratio': RATIO[tokens.card.imageRatio],
    '--th-grid-cols': String(tokens.productGrid.columnsDesktop),
    '--th-grid-cols-mobile': String(tokens.productGrid.columnsMobile),
  };
  const attrs: Record<string, string> = {
    'data-themed': '',
    'data-scheme': tokens.colorScheme === 'auto' && tokens.darkPalette ? 'auto' : 'light',
    'data-card': tokens.card.style,
    'data-header-layout': tokens.header.layout,
    'data-header-style': tokens.header.style,
    'data-header-sticky': tokens.header.sticky ? 'on' : 'off',
    'data-footer': tokens.footer.style,
    'data-gallery': tokens.productPage.galleryPosition,
    'data-blog': tokens.blog.layout,
    'data-input': tokens.form.inputStyle,
    'data-grid': String(tokens.productGrid.columnsDesktop),
    'data-grid-mobile': String(tokens.productGrid.columnsMobile),
  };
  return { style, attrs };
}
